/**
 * CardFeature - Componente reutilizável para exibição de features na Página Principal
 *
 * CONTEXTO DE NEGÓCIO:
 * Componente simples e claro que representa uma feature do sistema (ex: Análise Multi-Agent,
 * Análise de Petição Inicial). Deve ser reutilizável, acessível e seguir os padrões do projeto
 * (comentários exaustivos, nomes descritivos, TailwindCSS para estilização).
 *
 * PROPRIEDADES (Contract):
 * - title: Título exibido no card
 * - description: Descrição curta do que a feature faz
 * - icon?: JSX.Element opcional para exibir um ícone
 * - to?: string rota de navegação (se fornecida, o card é um Link)
 * - actionLabel?: string rótulo do botão de ação (padrão: "Abrir")
 *
 * ERROS/COMPORTAMENTO:
 * - Se `to` não for fornecido, o card renderiza um botão desabilitado por padrão.
 */

import React from 'react';
import { Link } from 'react-router-dom';

interface CardFeatureProps {
  title: string;
  description: string;
  icon?: React.ReactNode;
  to?: string;
  actionLabel?: string;
}

export function CardFeature({
  title,
  description,
  icon,
  to,
  actionLabel = 'Abrir',
}: CardFeatureProps): JSX.Element {
  // Renderiza um Link se 'to' estiver definido, caso contrário um container estático.
  const content = (
    <div className="flex h-full flex-col rounded-lg border border-gray-100 bg-white p-6 shadow-sm transition-shadow duration-200 hover:shadow-md">
      <div className="flex items-start gap-4">
        <div className="flex size-10 items-center justify-center text-primary-600">
          {icon}
        </div>

        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="mt-2 text-sm text-gray-600">{description}</p>
        </div>
      </div>

      <div className="mt-4 flex justify-end border-t border-gray-100 pt-4">
        <button
          className={`rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 ${
            to ? '' : 'cursor-not-allowed opacity-60'
          }`}
          disabled={!to}
        >
          {actionLabel}
        </button>
      </div>
    </div>
  );

  if (to) {
    return (
      <Link to={to} className="block h-full">
        {content}
      </Link>
    );
  }

  return <div className="block h-full">{content}</div>;
}

export default CardFeature;
